export const translations = {
  en: {
    nav: {
      home: "Home",
      skills: "Skills",
      projects: "Projects",
      cv: "CV",
      connect: "Let's Connect"
    },
    banner: {
      tagline: "Welcome to my Portfolio",
      greeting: "Hi! I'm Maram",
      roles: ["web developer", "web designer", "front-end developer", "back-end developer"],
      description: "I develop modern web applications with a focus on clean architecture, performance, and maintainable code. I care about building reliable systems, not just good-looking interfaces.",
      connect: "Let's Connect"
    },
    topBanner: {
      message: "This portfolio is currently being updated. Some features may be incomplete, but feel free to explore!"
    },
    skills: {
      title: "Skills & Expertise",
      intro: "Full Stack developer with experience in both Java/Spring Boot backends and Angular frontends, as well as PHP/Laravel. Comfortable with relational databases, REST APIs, authentication, and version control.",
      highlights: [
        "Java & Spring Boot Back-end",
        "Angular & TypeScript Front-end",
        "PHP & Laravel Web Applications",
        "Relational Databases & JDBC / JPA",
        "Secure REST APIs & JWT Auth",
        "Git & Collaborative DevOps"
      ],
      carousel: [
        "Back-end Development", "Front-end Development", "Performance & Responsiveness", "UI & Visual Design", "Full-stack Integration", "CMS Platforms"
      ],
      categories: {
        tools: { title: "Tools & DevOps", items: ["npm / Build Tools", "Git & GitHub Workflow", "Maven Dependencies", "Debugging & DevTools"] },
        databases: { title: "Databases & ORMs", items: ["MySQL & JDBC", "JPA & Hibernate ORM", "Relational DB Design", "ACID / Transactions"] },
        webFundamentals: { title: "Web Fundamentals", items: ["Responsive layouts", "Accessibility Basics", "Basic Security & CORS"] },
        backEnd: { title: "PHP & Laravel", items: ["PHP & Laravel Framework", "RESTful API Architecture", "Authentication / JWT", "Session Management"] },
        javaSpringBoot: { title: "Java & Spring Boot", items: ["Java OOP Principles", "Spring Boot Architecture", "Spring Security / JWT", "JPA Entity Mapping"] },
        angularFrontend: { title: "Angular & TypeScript", items: ["Components & Routing", "TypeScript Programming", "HttpClient REST Requests"] }
      }
    },
    projects: {
      title: "Projects & Case Studies",
      description: "Selected projects and upcoming case studies. Some entries are currently in progress and will be expanded with full technical breakdowns.",
      tabs: { all: "All", frontEnd: "Front-End", backEnd: "Back-End", fullStack: "Full-Stack" },
      status: {
        completed: "Completed",
        wip: "Work in Progress",
        live: "Live",
        placeholder: "Coming Soon"
      },
      headers: {
        technologies: "Technologies Used:",
        problem: "Problem",
        solution: "Solution",
        outcome: "Outcome",
        learned: "What I Learned",
        back: "Back to Projects",
        viewLive: "View Live",
        viewSource: "View Source",
        clickEnlarge: "Click to enlarge",
        note: "Important Note"
      }
    },
    contact: {
      title: "Get In Touch",
      placeholders: {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        phonePrefix: "Prefix",
        phone: "Phone No.",
        message: "Message"
      },
      button: { send: "Send", sending: "Sending..." },
      messages: {
        incomplete: "Please complete all fields.",
        invalidEmail: "Please enter a valid email address.",
        success: "Message sent successfully!",
        error: "Something went wrong, please try again later."
      }
    },
    current: {
      title: "Currently Working On",
      subtitle: "Coding in Public | Updated Monthly",
      lastUpdate: "Last update: May 2026",
      stages: {
        building: "Building",
        backend: "Backend Development",
        frontend: "Frontend Development",
        fullstack: "Full Stack Development",
        planning: "Planning",
        completed: "Completed"
      },
      items: {
        "lang-btn": { title: "Language Changer Button", desc: "Button to change the language of the portfolio website" },
        jobTracker: { title: "Simple Job Application Tracker", desc: "Log jobs you've applied to, status, notes, interviews. Genuinely useful for you right now and relatable to recruiters" },
        kivra: { title: "Kivra – Universal Media Tracker", desc: "All-in-one tracker for movies, TV shows, anime, manga and books, built with Java Spring Boot and Angular" },
        flashcards: { title: "Language Learning Flashcard App", desc: "Spaced repetition algorithm (shows cards you struggle with more often). Algorithmically interesting and useful" }
      }
    },
    cv: {
      backButton: "Back to Portfolio",
      role: "Full Stack Web Developer",
      download: "DOWNLOAD RESUME",
      about: {
        title: "ABOUT ME",
        greeting: "Hello, I'm Maram Aouini!",
        p1: "I am a Full Stack Web Developer focused on building reliable, user-friendly web applications. My expertise spans both modern frontend frameworks like React and Angular, and robust backend ecosystems including Java / Spring Boot and PHP / Laravel. I hold certifications in Full Stack and Back End Development from Talentform, as well as a Junior Java Developer certification from Generation Italy, and have hands-on experience working on real-world projects.",
        p2: "Coming from a background in technical assistance and customer service, I combine strong problem-solving skills with clear communication and a user-centric mindset. I enjoy managing complete, type-safe full-stack integrations, building secure RESTful APIs, and turning complex requirements into clean, maintainable solutions that actually work."
      },
      personalInfo: {
        title: "personal information",
        labels: {
          name: "FULL NAME",
          dob: "D.O.B.",
          address: "ADDRESS",
          email: "E-MAIL",
          phone: "PHONE",
          freelance: "WORK AVAILABILITY"
        },
        values: {
          freelance: "Available",
          dob: "13 May 2000"
        }
      },
      languages: {
        title: "languages",
        items: [
          { name: "English" },
          { name: "Italian" },
          { name: "Arabic" },
          { name: "Japanese" }
        ],
        levels: { native: "native", fluent: "fluent", intermediate: "intermediate", beginner: "beginner" }
      },
      certifications: {
        title: "CERTIFICATIONS",
        items: [
          { title: "Junior Java Developer", company: "Generation Italy", date: "2026", desc: "Intensive training program focused on Java OOP principles, database connectivity (JDBC/MySQL), enterprise backend design, and MVC architecture." },
          { title: "Full Stack Web Developer", company: "Talentform", date: "2025", desc: "Comprehensive training in modern web technologies including HTML5, CSS3, JavaScript ES6+, React, PHP, and MySQL." },
          { title: "Back End Developer", company: "Talentform", date: "2025", desc: "Focused curriculum on server-side programming, database management, API development, and security best practices." }
        ]
      },
      strengths: {
        title: "KEY TECHNICAL STRENGTHS",
        items: [
          "Ability to translate requirements into functional web solutions",
          "Strong problem-solving approach to debugging and errors",
          "Experience working with full authentication flows",
          "User-focused mindset from customer support background"
        ]
      },
      focus: {
        title: "CURRENT TECHNICAL FOCUS",
        items: [
          "Building robust Spring Boot RESTful backends and Angular frontends",
          "Improving relational database optimization and JPA entity mapping",
          "Strengthening React component architecture and state management",
          "Practicing clean, maintainable, and type-safe code patterns"
        ]
      },
      experience: {
        title: "WORK EXPERIENCE",
        items: [
          {
            title: "Web Designer",
            desc: "Contributed to web development projects for an international startup during an Erasmus traineeship. Built and customized responsive websites using Framer and WordPress, with rapid prototyping and testing via Replit in a remote team environment."
          },
          {
            title: "Warehouse Worker",
            desc: "Package management and goods handling through digital systems. Warehouse organization, compliance with safety procedures, and operational problem resolution."
          },
          {
            title: "Call Center, Technical Assistance",
            desc: "Technical assistance via inbound calls for electricity and gas utilities, invoice analysis, consumption monitoring, tariff plans, and energy sector documentation management."
          },
          {
            title: "Call Center, Technical Assistance",
            desc: "Technical assistance via inbound calls, invoice analysis, PayTV troubleshooting, TV schedule information, packages and documentation related to pay TV, CAM and various decoders."
          }
        ]
      }
    },
    // PROJECTS CONTENT - KEYED BY PROJECT ID
    projects_content: {
      1: {
        title: "Responsive Portfolio Interface",
        shortDescription: "My personal portfolio, built and deployed with React.",
        fullDescription: [
          "A fully responsive personal portfolio website built with React and Bootstrap. Showcases skills, projects, and contact information. Live demo available, with source code hosted on GitHub.",
          "This portfolio is a living project, continuously improved with new features and refactors."
        ],
        psa: {
          problem: "The goal was to present my profile, skills, and projects in a clean, professional way that is easy to navigate and scalable over time.",
          solution: "I designed and developed a responsive portfolio using React, focusing on component reusability, clear information hierarchy, and smooth user experience.",
          outcome: "A fast, accessible portfolio that clearly communicates my skills and can be extended with new projects and features."
        },
        learned: [
          "Managing state across components",
          "Creating reusable UI components",
          "Debugging async logic",
          "Testing React hooks",
          "Responsive layouts with Flexbox/Grid",
          "Using modals and carousels",
          "Integrating libraries like Bootstrap and icons",
          "Conditional rendering based on data",
          "Optimizing component performance",
          "Organizing project data dynamically"
        ]
      },
      2: {
        title: "404 Cat-Astrophe Interactive Page",
        shortDescription: "An interactive, animated 404 page with draggable cats and surprise Easter eggs.",
        fullDescription: [
          "This project is a fully interactive 404 error page built in React. Beyond a simple error message, it includes draggable elements, randomized animations, and a hidden Easter egg mode with a 10% chance of unlocking a secret message for playful user engagement.",
          "The page handles complex animation sequences, state management for multiple dynamic elements, and ensures smooth performance even with overlapping timers and transitions.",
          "It demonstrates advanced React skills including hooks, useRef management, useLayoutEffect for layout measurement, and precise control over component mounting and animations."
        ],
        liveNote: "Hint: Try refreshing or reloading the page multiple times in a row! There is a special 10% chance Easter Egg waiting to be triggered!",
        psa: {
          problem: "The goal was to create an engaging, interactive 404 page while maintaining smooth performance and predictable component behavior. Challenges included coordinating multiple animations, preventing layout flickering, and managing dynamic state for multiple draggable elements.",
          solution: "Implemented a controlled animation sequence with useLayoutEffect and useEffect to manage timing, used refs to track draggable cats and forbidden zones, and created fallback logic to ensure stable rendering. Added randomized messages and Easter eggs for enhanced user experience.",
          outcome: "Resulting page is visually dynamic, fully interactive, and robust against routing edge cases. Users can play with draggable cats, experience Easter egg surprises, and navigate back safely. The codebase now includes reusable patterns for complex interactive elements in React."
        },
        learned: [
          "Coordinated complex animations using React hooks.",
          "Managed multiple refs for interactive elements.",
          "Handled timers safely to avoid glitches.",
          "Created playful yet performant UI.",
          "Combined UX delight with technical stability."
        ]
      },
      3: {
        title: "Kivra – Universal Media Tracker",
        shortDescription: "An all-in-one tracker for anime, movies, TV shows, manga and books.",
        fullDescription: [
          "Kivra is a full-stack media tracking application built with Java Spring Boot and Angular. It allows users to manage and track all their media consumption: anime, movies, TV shows, manga and books, in a single unified platform.",
          "The project covers both frontend and backend development, including a REST API, user authentication, and a responsive Angular interface."
        ],
        psa: {
          problem: "Keeping track of media across different platforms and categories: anime, movies, books, manga, with no single tool that handles all of them.",
          solution: "Built a full-stack application with a Spring Boot REST API backend and an Angular frontend, allowing users to log and track all media types in one place.",
          outcome: "A functional, deployed media tracker that demonstrates full-stack development skills across both Java backend and Angular frontend."
        },
        learned: [
          "Architecting layered backends with Spring Boot Controller, Service, and Repository patterns.",
          "Securing REST APIs with Spring Security and JWT-based authentication.",
          "Building responsive single-page applications (SPA) using Angular components, services, and RxJS.",
          "Integrating third-party APIs (TMDB/AniList) to enrich media listings with real-time metadata.",
          "Designing relational database schemas and managing JPA entity mapping and migrations.",
          "Configured CORS policies and production environments for multi-host deployments."
        ],
        liveNote: "Unfortunately, due to the limitations of hosting on a free web service, the backend server spins down when inactive. When you first launch the live website, it can take up to 2 minutes for the server to wake up and start responding. I highly appreciate your patience!"
      },
      4: {
        title: "Grocery Tracking App",
        shortDescription: "(Coming Soon)",
        fullDescription: [
          "Full details, source code, and live demo will be added soon."
        ],
        psa: null,
        learned: []
      },
      5: {
        title: "User Management System",
        shortDescription: "(Planned Case Study)",
        fullDescription: [
          "Full details, source code, and live demo will be added soon."
        ],
        psa: null,
        learned: []
      },
      6: {
        title: "Full-Stack Web Platform",
        shortDescription: "(Case Study in Progress)",
        fullDescription: [
          "Full details, source code, and live demo will be added soon."
        ],
        psa: null,
        learned: []
      }
    },
    // WORKING ON CONTENT - KEYED BY ID STRING
    working_on_content: {
      "lang-btn": {
        title: "Language Changer Button",
        description: "A feature implementation granting users the ability to toggle the application language between English and Italian. Utilizes React Context API for global state management and dynamic content rendering.",
        roadmap: ["Implement Context", "Create Switcher UI", "Add Support for EN/IT", "Persist Choice"]
      },
      "jobTracker": {
        title: "Simple Job Application Tracker",
        description: "A practical tool designed to log and monitor job applications, dynamic interview schedules, follow-up alerts, and application outcomes. Genuinely useful for managing job search pipelines and relatable to recruiters.",
        roadmap: [
          "UI Design & Wireframing",
          "Core Job Entry Form",
          "Job List & Status Tracking",
          "Search, Filter & Sort",
          "Notes & Reminders per Job",
          "Stats & Application Overview"
        ]
      },
      "kivra": {
        title: "Kivra – Universal Media Tracker",
        description: "A comprehensive media tracking application allowing users to catalog and monitor their progress across movies, TV shows, anime, manga, and books. Featuring a robust, layered backend architecture built with Java Spring Boot and a dynamic, responsive client application built with Angular and TypeScript. Includes database integration, secure user authentication, and third-party media API integrations.",
        roadmap: ["Database Modeling & Schema Setup", "Spring Boot RESTful API Development", "Spring Security & JWT Authentication", "Angular Component Architecture", "Third-party API Integration", "UX/UI Improvements", "Bug Fixes & Refactoring"]
      },
      "flashcards": {
        title: "Language Learning Flashcard App",
        description: "An algorithmically interesting learning app utilizing spaced repetition intervals to schedule review sessions, ensuring that words you struggle with are reinforced more frequently.",
        roadmap: ["Spaced Repetition Algorithm", "Interactive Deck Creator", "Spaced Review Session", "Progress & Mastery Charting"]
      }
    },
  },
  it: {
    nav: {
      home: "Home",
      skills: "Competenze",
      projects: "Progetti",
      cv: "CV",
      connect: "Contattami"
    },
    banner: {
      tagline: "Benvenuto nel mio Portfolio",
      greeting: "Ciao! Sono Maram",
      roles: ["sviluppatrice web", "web designer", "sviluppatrice front-end", "sviluppatrice back-end"],
      description: "Sviluppo applicazioni web moderne concentrandomi su architettura pulita, prestazioni e codice manutenibile. Mi preoccupo di costruire sistemi affidabili, non solo interfacce belle da vedere.",
      connect: "Contattami"
    },
    topBanner: {
      message: "Questo portfolio è attualmente in aggiornamento. Alcune funzionalità potrebbero essere incomplete, ma sentiti libero di esplorare!"
    },
    skills: {
      title: "Competenze ed Esperienza",
      intro: "Sviluppatrice Full Stack con esperienza sia sui backend Java/Spring Boot che sui frontend Angular, così come in PHP/Laravel. A mio agio con database relazionali, API REST, autenticazione e controllo di versione.",
      highlights: [
        "Backend in Java / Spring Boot",
        "Frontend in Angular / TypeScript",
        "Web App in PHP / Laravel",
        "Database Relazionali e JDBC / JPA",
        "API REST Sicure e Auth JWT",
        "Git e DevOps Collaborativo"
      ],
      carousel: [
        "Sviluppo Back-end", "Sviluppo Front-end", "Performance e Responsività", "UI & Design Visivo", "Integrazione Full-stack", "Piattaforme CMS"
      ],
      categories: {
        tools: { title: "Strumenti e DevOps", items: ["npm / Strumenti di Build", "Flusso di Lavoro Git e GitHub", "Dipendenze Maven", "Debugging e DevTools"] },
        databases: { title: "Database e ORM", items: ["MySQL e JDBC", "JPA e ORM Hibernate", "Progettazione DB Relazionali", "ACID / Transazioni"] },
        webFundamentals: { title: "Fondamenti Web", items: ["Layout Responsive", "Basi di Accessibilità", "Sicurezza di Base e CORS"] },
        backEnd: { title: "PHP & Laravel", items: ["Framework PHP & Laravel", "Architettura API RESTful", "Autenticazione / JWT", "Gestione delle Sessioni"] },
        javaSpringBoot: { title: "Java & Spring Boot", items: ["Principi OOP Java", "Architettura Spring Boot", "Spring Security / JWT", "Mappatura Entità JPA"] },
        angularFrontend: { title: "Angular & TypeScript", items: ["Componenti & Routing", "Programmazione TypeScript", "HttpClient Richieste REST"] }
      }
    },
    projects: {
      title: "Progetti e Case Studies",
      description: "Progetti selezionati e case studies in arrivo. Alcune voci sono attualmente in corso e verranno ampliate con approfondimenti tecnici completi.",
      tabs: { all: "Tutti", frontEnd: "Front-End", backEnd: "Back-End", fullStack: "Full-Stack" },
      status: {
        completed: "Completato",
        wip: "In Lavorazione",
        live: "Online",
        placeholder: "In Arrivo"
      },
      headers: {
        technologies: "Tecnologie Utilizzate:",
        problem: "Problema",
        solution: "Soluzione",
        outcome: "Risultato",
        learned: "Cosa ho imparato",
        back: "Torna ai Progetti",
        viewLive: "Vedi Live",
        viewSource: "Vedi Codice",
        clickEnlarge: "Clicca per ingrandire",
        note: "Nota Importante"
      }
    },
    contact: {
      title: "Contattami",
      placeholders: {
        firstName: "Nome",
        lastName: "Cognome",
        email: "Indirizzo Email",
        phonePrefix: "Prefisso",
        phone: "N. Telefono",
        message: "Messaggio"
      },
      button: { send: "Invia", sending: "Invio in corso..." },
      messages: {
        incomplete: "Per favore compila tutti i campi.",
        invalidEmail: "Per favore inserisci un indirizzo email valido.",
        success: "Messaggio inviato con successo!",
        error: "Qualcosa è andato storto, per favore riprova più tardi."
      }
    },
    current: {
      title: "Attualmente al Lavoro su",
      subtitle: "Sviluppo in Pubblico | Aggiornato Mensilmente",
      lastUpdate: "Ultimo aggiornamento: Maggio 2026",
      stages: {
        building: "In Costruzione",
        backend: "Sviluppo Back-end",
        frontend: "Sviluppo Front-end",
        fullstack: "Sviluppo Full Stack",
        planning: "Pianificazione",
        completed: "Completato"
      },
      items: {
        "lang-btn": { title: "Pulsante Cambio Lingua", desc: "Pulsante per cambiare la lingua del sito portfolio" },
        jobTracker: { title: "Tracker per Candidature di Lavoro", desc: "Registra i lavori per cui ti sei candidato, stato, note, colloqui. Utilissimo per te in questo momento e d'impatto per i recruiter" },
        kivra: { title: "Kivra – Media Tracker Universale", desc: "Tracker tutto-in-uno per film, serie TV, anime, manga e libri, realizzato con Java Spring Boot e Angular" },
        flashcards: { title: "App di Flashcard per Lingue", desc: "Algoritmo di ripetizione dilazionata (mostra più spesso le carte difficili). Algoritmicamente interessante e utile" }
      }
    },
    cv: {
      backButton: "Torna al Portfolio",
      role: "Sviluppatrice Web Full Stack",
      download: "SCARICA CURRICULUM",
      about: {
        title: "CHI SONO",
        greeting: "Ciao, sono Maram Aouini!",
        p1: "Sono una sviluppatrice Web Full Stack specializzata nella creazione di applicazioni web affidabili e user-friendly. Le mie competenze spaziano dai moderni framework frontend come React e Angular, a robusti ecosistemi backend tra cui Java / Spring Boot e PHP / Laravel. Possiedo certificazioni in Sviluppo Full Stack e Back End presso Talentform, oltre a una certificazione come Junior Java Developer rilasciata da Generation Italy, e ho esperienza pratica su progetti reali.",
        p2: "Provenendo da un background di assistenza tecnica e servizio clienti, unisco forti capacità di problem-solving a una comunicazione chiara e una mentalità orientata all'utente. Mi piace gestire integrazioni full-stack complete e tipizzate, creare API REST sicure e trasformare requisiti complessi in soluzioni pulite e manutenibili che funzionano davvero."
      },
      personalInfo: {
        title: "informazioni personali",
        labels: {
          name: "NOME",
          dob: "DATA DI NASCITA",
          address: "INDIRIZZO",
          email: "E-MAIL",
          phone: "TELEFONO",
          freelance: "DISPONIBILITÀ"
        },
        values: {
          freelance: "Disponibile",
          dob: "13 Maggio 2000"
        }
      },
      languages: {
        title: "lingue",
        items: [
          { name: "Inglese" },
          { name: "Italiano" },
          { name: "Arabo" },
          { name: "Giapponese" }
        ],
        levels: { native: "madrelingua", fluent: "fluente", intermediate: "intermedio", beginner: "base" }
      },
      certifications: {
        title: "CERTIFICAZIONI",
        items: [
          { title: "Junior Java Developer", company: "Generation Italy", date: "2026", desc: "Corso di formazione intensivo focalizzato sui principi OOP Java, connessione database (JDBC/MySQL), sviluppo backend enterprise e architettura MVC." },
          { title: "Sviluppatrice Web Full Stack", company: "Talentform", date: "2025", desc: "Formazione completa in tecnologie web moderne inclusi HTML5, CSS3, JavaScript ES6+, React, PHP e MySQL." },
          { title: "Sviluppatrice Back End", company: "Talentform", date: "2025", desc: "Curriculum focalizzato su programmazione server-side, gestione database, sviluppo API e best practices di sicurezza." }
        ]
      },
      strengths: {
        title: "PUNTI DI FORZA TECNICI",
        items: [
          "Capacità di tradurre requisiti in soluzioni web funzionali",
          "Forte approccio al problem-solving per debugging ed errori",
          "Esperienza con flussi di autenticazione completi",
          "Mentalità incentrata sull'utente derivante dal background nel supporto clienti"
        ]
      },
      focus: {
        title: "FOCUS TECNICO ATTUALE",
        items: [
          "Costruzione di solidi backend Spring Boot REST e frontend Angular",
          "Miglioramento dell'ottimizzazione dei database relazionali e mappatura JPA",
          "Rafforzamento dell'architettura dei componenti React e gestione dello stato",
          "Pratica di pattern di codice puliti, manutenibili e tipizzati"
        ]
      },
      experience: {
        title: "ESPERIENZA LAVORATIVA",
        items: [
          {
            title: "Web Designer",
            desc: "Contribuito a progetti di sviluppo web per una startup internazionale durante un tirocinio Erasmus. Costruito e personalizzato siti responsive usando Framer e WordPress, con prototipazione rapida e testing via Replit in un ambiente remoto."
          },
          {
            title: "Addetta al Magazzino",
            desc: "Gestione pacchi e movimentazione merci tramite sistemi digitali. Organizzazione magazzino, rispetto procedure sicurezza e risoluzione problemi operativi."
          },
          {
            title: "Call Center, Assistenza Tecnica",
            desc: "Assistenza tecnica via chiamate inbound per utenze luce e gas, analisi fatture, monitoraggio consumi, piani tariffari e gestione documentazione settore energetico."
          },
          {
            title: "Call Center, Assistenza Tecnica",
            desc: "Assistenza tecnica via chiamate inbound, analisi fatture, troubleshooting PayTV, informazioni palinsesto, pacchetti e documentazione relativa a pay TV, CAM e vari decoder."
          }
        ]
      }
    },
    // PROJECTS CONTENT - ITALIAN
    projects_content: {
      1: {
        title: "Interfaccia Portfolio Responsiva",
        shortDescription: "Il mio portfolio personale, costruito e pubblicato con React.",
        fullDescription: [
          "Un sito web portfolio personale completamente responsivo costruito con React e Bootstrap. Mostra competenze, progetti e informazioni di contatto. Demo live disponibile, con codice sorgente ospitato su GitHub.",
          "Questo portfolio è un progetto in evoluzione, continuamente migliorato con nuove funzionalità e refactoring."
        ],
        psa: {
          problem: "L'obiettivo era presentare il mio profilo, le competenze e i progetti in modo pulito e professionale, facile da navigare e scalabile nel tempo.",
          solution: "Ho progettato e sviluppato un portfolio responsivo utilizzando React, concentrandomi sulla riutilizzabilità dei componenti, una chiara gerarchia delle informazioni e un'esperienza utente fluida.",
          outcome: "Un portfolio veloce e accessibile che comunica chiaramente le mie competenze e può essere esteso con nuovi progetti e funzionalità."
        },
        learned: [
          "Gestione dello stato tra i componenti",
          "Creazione di componenti UI riutilizzabili",
          "Debug della logica asincrona",
          "Testing degli hook di React",
          "Layout responsivi con Flexbox/Grid",
          "Utilizzo di modali e caroselli",
          "Integrazione di librerie come Bootstrap e icone",
          "Rendering condizionale basato sui dati",
          "Ottimizzazione delle prestazioni dei componenti",
          "Organizzazione dinamica dei dati del progetto"
        ]
      },
      2: {
        title: "Pagina Interattiva 404 Cat-Astrophe",
        shortDescription: "Una pagina 404 interattiva e animata con gatti trascinabili e Easter egg a sorpresa.",
        fullDescription: [
          "Questo progetto è una pagina di errore 404 completamente interattiva costruita in React. Oltre a un semplice messaggio di errore, include elementi trascinabili, animazioni casuali e una modalità 'Easter egg' nascosta con il 10% di possibilità di sbloccare un messaggio segreto per coinvolgere l'utente.",
          "La pagina gestisce sequenze di animazione complesse, gestione dello stato per multipli elementi dinamici e garantisce prestazioni fluide anche con timer e transizioni sovrapposte.",
          "Dimostra competenze avanzate di React, inclusi hook, gestione useRef, useLayoutEffect per la misurazione del layout e controllo preciso sul mounting e le animazioni dei componenti."
        ],
        liveNote: "Suggerimento: Prova ad aggiornare o ricaricare la pagina più volte di seguito! C'è un Easter Egg speciale con il 10% di probabilità di essere attivato!",
        psa: {
          problem: "L'obiettivo era creare una pagina 404 coinvolgente e interattiva mantenendo prestazioni fluide e un comportamento prevedibile dei componenti. Le sfide includevano il coordinamento di animazioni multiple, la prevenzione dello sfarfallio del layout e la gestione dello stato dinamico per più elementi trascinabili.",
          solution: "Implementata una sequenza di animazione controllata con useLayoutEffect e useEffect per gestire i tempi, utilizzati ref per tracciare i gatti trascinabili e le zone proibite, e creata una logica di fallback per garantire un rendering stabile. Aggiunti messaggi casuali ed Easter egg per una migliore esperienza utente.",
          outcome: "La pagina risultante è visivamente dinamica, completamente interattiva e robusta contro i casi limite di routing. Gli utenti possono giocare con i gatti trascinabili, scoprire sorprese Easter egg e navigare indietro in sicurezza. Il codice include ora pattern riutilizzabili per elementi interattivi complessi in React."
        },
        learned: [
          "Coordinamento di animazioni complesse usando gli hook di React.",
          "Gestione di ref multipli per elementi interattivi.",
          "Gestione di timer sicura per evitare glitch.",
          "Creazione di UI giocosa ma performante.",
          "Combinazione di divertimento UX con stabilità tecnica."
        ]
      },
      3: {
        title: "Kivra – Media Tracker Universale",
        shortDescription: "Un tracker tutto-in-uno per anime, film, serie TV, manga e libri.",
        fullDescription: [
          "Kivra è un'applicazione per il tracciamento dei media full-stack realizzata con Java Spring Boot e Angular. Consente agli utenti di gestire e tracciare tutti i propri consumi multimediali: anime, film, serie TV, manga e libri, in un'unica piattaforma unificata.",
          "Il progetto copre sia lo sviluppo frontend che backend, comprese le API REST, l'autenticazione utente e un'interfaccia Angular reattiva."
        ],
        psa: {
          problem: "Tenere traccia dei media su diverse piattaforme e categorie: anime, film, libri, manga — senza un unico strumento che li gestisca tutti.",
          solution: "Sviluppato un'applicazione full-stack con un backend API REST in Spring Boot e un frontend Angular, consentendo agli utenti di registrare e tracciare tutti i tipi di media in un unico posto.",
          outcome: "Un tracker multimediale funzionale e pubblicato che dimostra le competenze di sviluppo full-stack sia sul backend Java che sul frontend Angular."
        },
        learned: [
          "Architettare backend stratificati con i pattern Spring Boot Controller, Service e Repository.",
          "Proteggere le API REST con Spring Security e autenticazione basata su JWT.",
          "Costruire applicazioni a pagina singola (SPA) reattive utilizzando componenti, servizi e RxJS in Angular.",
          "Integrare API di terze parti (TMDB/AniList) per arricchire gli elenchi dei media con metadati in tempo reale.",
          "Progettare schemi di database relazionali e gestire la mappatura e le migrazioni delle entità JPA.",
          "Configurare policy CORS e ambienti di produzione per distribuzioni multi-host."
        ],
        liveNote: "Sfortunatamente, a causa delle limitazioni dell'hosting su un servizio web gratuito, il server backend si spegne quando inattivo. All'avvio del sito live, potrebbero essere necessari fino a 2 minuti affinché il server si riattivi e risponda. Vi ringrazio per la pazienza!"
      },
      4: {
        title: "App Tracciamento Spesa",
        shortDescription: "(In Arrivo)",
        fullDescription: [
          "Dettagli completi, codice sorgente e demo live verranno aggiunti presto."
        ],
        psa: null,
        learned: []
      },
      5: {
        title: "Sistema Gestione Utenti",
        shortDescription: "(Case Study Pianificato)",
        fullDescription: [
          "Dettagli completi, codice sorgente e demo live verranno aggiunti presto."
        ],
        psa: null,
        learned: []
      },
      6: {
        title: "Piattaforma Web Full-Stack",
        shortDescription: "(Case Study in Lavorazione)",
        fullDescription: [
          "Dettagli completi, codice sorgente e demo live verranno aggiunti presto."
        ],
        psa: null,
        learned: []
      }
    },
    // WORKING ON CONTENT - ITALIAN
    working_on_content: {
      "lang-btn": {
        title: "Pulsante Cambio Lingua",
        description: "Implementazione di una funzionalità che permette agli utenti di cambiare la lingua dell'applicazione tra Inglese e Italiano. Utilizza React Context API per la gestione dello stato globale.",
        roadmap: ["Implementare Context", "Creare UI Switcher", "Aggiungere Supporto EN/IT", "Salvare Preferenza"]
      },
      "jobTracker": {
        title: "Tracker per Candidature di Lavoro",
        description: "Uno strumento pratico progettato per registrare e monitorare le candidature di lavoro, i calendari dei colloqui, i promemoria di follow-up e i risultati. Genuinamente utile per gestire la ricerca di lavoro e facilmente relazionabile per i recruiter.",
        roadmap: [
          "Design UI & Wireframing",
          "Modulo di Inserimento Lavoro",
          "Lista Lavori & Tracciamento Stato",
          "Ricerca, Filtri & Ordinamento",
          "Note & Promemoria per Lavoro",
          "Statistiche & Panoramica Candidature"
        ]
      },
      "kivra": {
        title: "Kivra – Media Tracker Universale",
        description: "Un'applicazione completa per il tracciamento dei media che consente agli utenti di catalogare e monitorare i propri progressi tra film, serie TV, anime, manga e libri. Caratterizzata da una robusta architettura backend stratificata in Java Spring Boot e da un'applicazione client dinamica e reattiva in Angular e TypeScript. Include l'integrazione di database, autenticazione utente sicura e integrazioni con API multimediali esterne.",
        roadmap: ["Modellazione del Database e Setup Schema", "Sviluppo di API RESTful in Spring Boot", "Spring Security e Autenticazione JWT", "Architettura dei Componenti Angular", "Integrazione API Esterne", "Miglioramenti UX/UI", "Risoluzione Bug e Refactoring"]
      },
      "flashcards": {
        title: "App di Flashcard per Lingue",
        description: "Un'applicazione di apprendimento super interessante basata su algoritmi che utilizza intervalli di ripetizione dilazionata per programmare le sessioni di ripasso, assicurando che i vocaboli con cui si ha più difficoltà vengano proposti più frequentemente.",
        roadmap: ["Algoritmo Ripetizione Dilazionata", "Creatore di Deck Interattivo", "Sessione di Ripasso Dilazionata", "Grafici di Progresso e Competenza"]
      }
    }
  }
};
